/**
 * FASE 149 (149.1) — dominio de MODELO e EFFORT do Codex (OpenAI), PARALELO ao dominio `model.ts`/
 * `effort.ts` (Anthropic/claude) — NAO reusa `ModelId`/`EffortLevel` (mesmo tratamento arquitetural ja
 * dado ao Grok Build na FASE 125.A: o Codex tem espaco de modelos PROPRIO, gerido pela sua propria flag
 * `-m`/config `model_reasoning_effort`, nunca por `ANTHROPIC_MODEL`/`CLAUDE_CODE_EFFORT_LEVEL`).
 *
 * IDs e niveis de effort CONFIRMADOS EMPIRICAMENTE (2026-07-13, `codex --version` 0.144.1 +
 * `~/.codex/models_cache.json`, ETAG presente/fresco): ver plan_investigation.md secao "FASE 149" para a
 * evidencia completa. "GPT 5.6 Sol"/"Terra" (nomes do usuario) e "X-Ray" (effort, de memoria aproximada)
 * eram nomes/grafias de voz — os IDs/niveis reais estao abaixo ("xhigh", nao "X-Ray").
 *
 * DIFERENCA DE SEMANTICA vs o dominio claude (simplificacao deliberada): o `codex`/`codex resume --last`
 * aceita `-m`/`-c model_reasoning_effort=` em AMBOS fresh e resume (confirmado via `codex --help` e `codex
 * resume --help`) — ao contrario do claude (onde o cache de prompt e keyed por (model,effort) e reinjetar
 * errado quebra o cache), aqui NAO ha necessidade de um conceito "registered" (auto-align na retomada):
 * o valor PREFERIDO e sempre re-aplicado no spawn, fresh ou resume, sem risco de drift de cache.
 */
import { z } from 'zod';
import { PROJECT_CODEX_MODEL, PROJECT_CODEX_EFFORT } from './launch-model-policy.js';
export { PROJECT_CODEX_EFFORT } from './launch-model-policy.js';
/**
 * IDs canonicos (slugs reais da API) dos modelos Codex que o term-deck gerencia. Fonte:
 * `~/.codex/models_cache.json` (cache mantido pelo proprio binario codex, refletindo o backend da OpenAI).
 * `codex-auto-review` (modelo interno, `visibility: "hide"`) e deliberadamente EXCLUIDO.
 */
export const CODEX_MODEL_IDS = [
    // M488.12b — `gpt-6-astra` CONFIRMADO na config real do Codex CLI 0.153.0
    // (`[tui.model_availability_nux]` do ~/.codex/config.toml), que e a secao pela qual o proprio CLI
    // anuncia disponibilidade de modelo. Nao foi digitado de memoria: o double check do lote exigiu
    // verificar o id ANTES de expor, porque catalogo que oferece modelo recusado pelo CLI e opcao quebrada.
    'gpt-6-astra',
    // 22/09/2026 — GPT-6 Sol e GPT-6 Luna apareceram no `models_cache.json` do CLI (visibility list) e o detector de
    // catálogo do ciclo de vida os acusou. Mesmo nível, geração nova: superam GPT-5.6 Sol/Luna (model-lifecycle).
    'gpt-6-sol',
    'gpt-6-luna',
    // 30/09/2026 — GPT-6.1 Sol no `models_cache.json` do Codex 0.159.0 (visibility list, 272 mil, low..ultra) e modelo do
    // `~/.codex/config.toml` do dono. Mesmo nível, geração maior: passa a ser o Sol vigente (latestCodexOfTier).
    'gpt-6.1-sol',
    // 26/10/2026 — cache CLI observado: visibility list, 500000 tokens, low..ultra (max explicito).
    'gpt-6.1-terra',
    'gpt-5.6-sol',
    'gpt-5.6-terra',
    'gpt-5.6-luna',
    'gpt-5.5',
    'gpt-5.4',
    'gpt-5.4-mini',
];
export const codexModelIdSchema = z.enum(CODEX_MODEL_IDS);
/** Type guard para um CodexModelId vindo de fonte nao-tipada (SQLite/JSON/env). */
export function isCodexModelId(v) {
    return typeof v === 'string' && CODEX_MODEL_IDS.includes(v);
}
/** Rotulo curto e legivel (chip/tooltip da UI). Mapeamento conceitual do usuario: Sol~=Fable, Terra~=Sonnet. */
export const CODEX_MODEL_LABELS = {
    'gpt-6-astra': 'GPT-6 Astra',
    'gpt-6-sol': 'GPT-6 Sol',
    'gpt-6.1-sol': 'GPT-6.1 Sol',
    'gpt-6.1-terra': 'GPT-6.1 Terra',
    'gpt-6-luna': 'GPT-6 Luna',
    'gpt-5.6-sol': 'GPT-5.6 Sol',
    'gpt-5.6-terra': 'GPT-5.6 Terra',
    'gpt-5.6-luna': 'GPT-5.6 Luna',
    'gpt-5.5': 'GPT-5.5',
    'gpt-5.4': 'GPT-5.4',
    'gpt-5.4-mini': 'GPT-5.4 Mini',
};
/** Janela de contexto confirmada (tokens) — so os 3 modelos da familia 5.6 foram medidos empiricamente. */
export const CODEX_MODEL_CONTEXT_WINDOW = {
    'gpt-6-sol': 272_000,
    'gpt-6.1-sol': 272_000,
    'gpt-6.1-terra': 500_000,
    'gpt-6-luna': 272_000,
    'gpt-5.6-sol': 272_000,
    'gpt-5.6-terra': 272_000,
    'gpt-5.6-luna': 272_000,
};
/** Normaliza um slug cru (ou apelido curto "sol"/"terra"/"luna") para um CodexModelId gerido, ou `null`. */
export function normalizeCodexModel(raw) {
    if (typeof raw !== 'string')
        return null;
    const v = raw.trim().toLowerCase();
    if (v.length === 0)
        return null;
    if (isCodexModelId(v))
        return v;
    if (v === 'gpt-6')
        return latestCodexOfTier('astra');
    // Apelido de NÍVEL = a versão mais nova daquele nível no catálogo (como o alias de família do Claude).
    if (isCodexTier(v))
        return latestCodexOfTier(v);
    return null;
}
/**
 * 22/09/2026 — NÍVEL e GERAÇÃO do Codex derivados do id (`gpt-<geração>-<nível>`). Sol, Terra, Luna e Astra
 * convivem como níveis; dentro de um mesmo nível a geração maior supera a menor (GPT-6 Sol > GPT-5.6 Sol),
 * exatamente como a família no Claude. Ids sem nível (`gpt-5.5`, `gpt-5.4-mini`) seguem só o `upgrade` do provedor.
 */
export const CODEX_TIERS = ['astra', 'sol', 'terra', 'luna'];
export function isCodexTier(v) {
    return typeof v === 'string' && CODEX_TIERS.includes(v);
}
export function codexTierOf(id) {
    const match = id?.match(/^gpt-[\d.]+-(astra|sol|terra|luna)$/u);
    return match ? match[1] : null;
}
function codexGenerationOf(id) {
    return (id.match(/^gpt-([\d.]+)-/u)?.[1] ?? '').split('.').map((n) => Number.parseInt(n, 10)).filter(Number.isFinite);
}
function newerGeneration(a, b) {
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
        if ((a[i] ?? 0) !== (b[i] ?? 0))
            return (a[i] ?? 0) > (b[i] ?? 0);
    }
    return false;
}
/** A versão VIGENTE de um nível que o catálogo garante ter (política referencia o nível, nunca um id). */
export function currentCodexTier(tier) {
    const model = latestCodexOfTier(tier);
    if (!model)
        throw new Error(`codex_tier_sem_modelo:${tier}`);
    return model;
}
/** A versão mais nova de um nível no catálogo (ou null se o nível não existe). */
export function latestCodexOfTier(tier) {
    let best = null;
    for (const id of CODEX_MODEL_IDS) {
        if (codexTierOf(id) !== tier)
            continue;
        if (!best || newerGeneration(codexGenerationOf(id), codexGenerationOf(best)))
            best = id;
    }
    return best;
}
/**
 * Niveis de reasoning-effort do Codex (superconjunto). Confirmado via `models_cache.json`:
 * Sol/Terra suportam os 6; Luna vai ate `max` (sem `ultra`); 5.5/5.4/5.4-mini vao ate `xhigh`.
 * "ultra" = "Maximum reasoning with automatic task delegation" (achado do double-check — comportamento
 * exato nao validado empiricamente ainda; nao usar como default sem validacao — ver 149.5).
 */
export const CODEX_EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'];
export const codexEffortLevelSchema = z.enum(CODEX_EFFORT_LEVELS);
export function isCodexEffortLevel(v) {
    return typeof v === 'string' && CODEX_EFFORT_LEVELS.includes(v);
}
/** Niveis validos de effort PARA UM MODELO especifico (varia por familia — ver docstring acima). */
export function codexEffortLevelsForModel(model) {
    // Por NÍVEL (conferido no models_cache.json de 22/09/2026 para as gerações 5.6 e 6): Luna vai até `max`.
    const tier = codexTierOf(model);
    if (tier === 'astra' || tier === 'sol' || tier === 'terra')
        return CODEX_EFFORT_LEVELS;
    if (tier === 'luna')
        return ['low', 'medium', 'high', 'xhigh', 'max'];
    // gpt-5.5 / gpt-5.4 / gpt-5.4-mini / desconhecido: conjunto conservador confirmado (sem max/ultra).
    return ['low', 'medium', 'high', 'xhigh'];
}
/**
 * O launch deste provider/kind suporta gestao de modelo/effort Codex pelo term-deck? So o provider
 * `codex` (terminal OU orquestrador/master — FASE 149.2 destrava o master no Codex como OPCAO).
 */
export function providerSupportsCodexModel(provider, _kind = 'terminal') {
    return provider === 'codex';
}
/** Effort segue a mesma regra de suporte que o modelo (mesmo binario, mesmo mecanismo de config). */
export function providerSupportsCodexEffort(provider, kind = 'terminal') {
    return providerSupportsCodexModel(provider, kind);
}
/**
 * POLITICA de modelo Codex no launch. Retorna o CodexModelId a aplicar, ou `null` (N/A / sem preferencia
 * nem default — o resolver entao NAO injeta `-m`, o codex usa o default do PROPRIO `~/.codex/config.toml`).
 * Precedencia: 1) provider nao suporta -> null. 2) override explicito. 3) preferido do projeto.
 * 4) default de fabrica sugerido.
 */
export function decideLaunchCodexModel(input) {
    if (!providerSupportsCodexModel(input.provider, input.kind ?? 'terminal'))
        return null;
    if (input.explicitOverride)
        return input.explicitOverride;
    return input.preferred ?? input.categoryDefault ?? input.defaultModel ?? null;
}
/** POLITICA de effort Codex no launch. Mesma precedencia de `decideLaunchCodexModel`. */
export function decideLaunchCodexEffort(input) {
    if (!providerSupportsCodexEffort(input.provider, input.kind ?? 'terminal'))
        return null;
    if (input.explicitOverride)
        return input.explicitOverride;
    return input.preferred ?? input.categoryDefault ?? input.defaultEffort ?? null;
}
/**
 * Nomes das variaveis de ambiente que o term-deck injeta por spawn — consumidas pela funcao
 * `Invoke-CodexCommand` do `restore-claude-bootstrap.ps1` (repo irmao `restore-claude-windows-terminal`,
 * FASE 149.1.3), que as traduz para `-m <slug> -c model_reasoning_effort=<nivel>`. NAO confundir com
 * `ANTHROPIC_MODEL`/`CLAUDE_CODE_EFFORT_LEVEL` (dominio claude, MODEL_ENV_VAR/EFFORT_ENV_VAR em
 * model.ts/effort.ts) — o Codex NUNCA le essas.
 */
export const CODEX_MODEL_ENV_VAR = 'TERM_DECK_CODEX_MODEL';
export const CODEX_EFFORT_ENV_VAR = 'TERM_DECK_CODEX_EFFORT';
/**
 * Default vigente do dono: Terra/max para aberturas sem escolha explícita (26/10/2026).
 * Compartilhado pelo workbench, categoria e launch de projeto; não modifica processos vivos.
 */
export const DEFAULT_CODEX_MODEL = PROJECT_CODEX_MODEL;
export const DEFAULT_CODEX_EFFORT = PROJECT_CODEX_EFFORT;
//# sourceMappingURL=codex-model.js.map